<script setup>
import {onMounted, ref} from 'vue';
import ClientService from '@/services/ClientService';
import {getApiBaseUrl, setButtonContentDefault, setButtonContentLoading, setButtonsContentDefault, setButtonsContentLoading, setButtonsContentSuccess, showErrorMessage, showSuccessMessage} from '@/helper';
import {checkForBankIdLogin, getBankIdUrl, getLoggedUser} from '@/services/AuthService';
import InvestmentQuestionnaireService, {visibleInvestmentQuestionnaireResultKeys} from '@/services/InvestmentQuestionnaireService';
import MeetingRecordService from '@/services/MeetingRecordService';
import DynamicForm from '@/views/DynamicForm.vue';
import DocumentsListMinimalComponent from '@/components/documents/DocumentsListMinimalComponent.vue';
import AmlQuestionnaireService, {visibleAMLQuestionnaireResultKeys} from '@/services/AmlQuestionnaireService';
import FormService from '@/services/FormService';
import {Capacitor} from '@capacitor/core';
import {Browser} from '@capacitor/browser';
import IdentificationService from '@/services/IdentificationService';
import CustomDropzone from '@/components/forms/CustomDropzone.vue';
import {ErrorMessage, Field, Form} from 'vee-validate';

import {useI18n} from 'vue-i18n';
import AppAnnouncements from '@/components/AppAnnouncements.vue';

const {t, te} = useI18n();

const props = defineProps({});


const EMPTY_ADDRESS = {
  street: '',
  city: '',
  zip_code: '',
  country: ''
};
const EMPTY_BANK_ACCOUNT = {
  currency: 'CZK',
  account_number: '',
  bank_code: '',
  iban: '',
  bic: ''
};

const formDenied = ref(false);
const steps = ref([]);
const step = ref({type: 'start', title: t('clientOnboardingOverlay.requirements_to_processed')});
const stepIndex = ref(0);
const loggedUser = ref(null);
const loggedClient = ref(null);
const emit = defineEmits(['onboardingDone']);
const addedFiles = ref([]);
const result = ref(null);

onMounted(() => {
  refreshLoggedUser();
  emitter.on('noDocuments', () => {
    nextStep();
  });
});

function refreshLoggedUser() {
  getLoggedUser().then(response => {
    loggedClient.value = response.client;
    loggedUser.value = response;
    prepareSteps();
  });
}

function prepareSteps() {
  const allSteps = [{type: 'start', title: t('clientOnboardingOverlay.requirements_to_processed')}];
  if (loggedUser.value?.meta?.show_form) {
    const onlyClient = {...loggedClient.value};
    delete onlyClient.addresses;
    delete onlyClient.address;
    delete onlyClient.bank_accounts;
    allSteps.push({
      type: 'complex_form', title: t('clientOnboardingOverlay.confirmation_data'), formData: {
        client: onlyClient,
        domicile_address: {...EMPTY_ADDRESS, ...loggedClient.value.domicile_address},
        correspondence_address: {...EMPTY_ADDRESS, ...loggedClient.value.correspondence_address},
        bank_account: {...EMPTY_BANK_ACCOUNT, ...loggedClient.value.bank_accounts?.[0]},
      }
    });
  }

  if (loggedUser.value?.waiting_identifications?.length > 0) {
    for (const record of loggedUser.value.waiting_identifications) {
      allSteps.push({id: record.id, type: 'identification', title: 'Identifikace', formData: record});
    }
  }

  if (loggedUser.value?.waiting_investment_questionnaires?.length > 0) {
    for (const questionnaire of loggedUser.value.waiting_investment_questionnaires) {
      allSteps.push({id: questionnaire.id, type: 'investment_questionnaire', title: t('clientOnboardingOverlay.investment_questionnaire'), formData: questionnaire});
    }
  }

  if (loggedUser.value?.waiting_meeting_records?.length > 0) {
    for (const record of loggedUser.value.waiting_meeting_records) {
      allSteps.push({id: record.id, type: 'meeting_record', title: t('clientOnboardingOverlay.meeting_records'), formData: record});
    }
  }

  if (loggedUser.value?.waiting_aml_questionnaires?.length > 0) {
    for (const record of loggedUser.value.waiting_aml_questionnaires) {
      allSteps.push({id: record.id, type: 'aml_questionnaire', title: t('clientOnboardingOverlay.aml_questionnaire'), formData: record});
    }
  }

  if (loggedUser.value?.waiting_forms?.length > 0) {
    for (const record of loggedUser.value.waiting_forms) {
      allSteps.push({id: record.id, type: 'form', title: t('clientOnboardingOverlay.form'), formData: record});
    }
  }

  if (loggedUser.value?.meta?.documents_to_sign > 0) {
    allSteps.push({type: 'document_sign', title: t('clientOnboardingOverlay.document_signature')});
  }

  allSteps.push({type: 'end', title: t('clientOnboardingOverlay.end')});
  steps.value = allSteps;
}

function submitComplexForm() {
  setButtonsContentLoading('btn-primary');
  ClientService.saveClientComplex(step.value.formData, loggedClient.value.id).then(response => {
    setButtonsContentSuccess('btn-primary');
    showSuccessMessage(response.message);
    nextStep();
  }, () => {
    // Hlášku už zobrazil ClientService (showErrorMessage) - tady jen odemkneme tlačítko.
    setButtonsContentDefault('btn-primary');
  });
}

function submitInvestmentQuestionnaire() {
  const body = {
    ...step.value.formData,
    state: formDenied.value ? 'DENIED' : 'APPROVED'
  };
  setButtonsContentLoading('btn-primary');
  InvestmentQuestionnaireService.saveQuestionnaire(loggedClient.value.id, body).then(response => {
    setButtonsContentSuccess('btn-primary');
    showSuccessMessage(response.message);
    nextStep();
  }, () => {
    setButtonsContentDefault('btn-primary');
  });
}

function submitMeetingRecord() {
  const body = {
    ...step.value.formData,
    state: formDenied.value ? 'DENIED' : 'APPROVED'
  };
  setButtonsContentLoading('btn-primary');
  MeetingRecordService.saveMeetingRecord(loggedClient.value.id, body).then(response => {
    setButtonsContentSuccess('btn-primary');
    showSuccessMessage(response.message);
    nextStep();
  }, () => {
    setButtonsContentDefault('btn-primary');
  });
}

function submitAmlQuestionnaire() {
  const body = {
    ...step.value.formData,
    state: formDenied.value ? 'DENIED' : 'APPROVED'
  };
  setButtonsContentLoading('btn-primary');
  AmlQuestionnaireService.saveQuestionnaire(loggedClient.value.id, body).then(response => {
    setButtonsContentSuccess('btn-primary');
    showSuccessMessage(response.message);
    nextStep();
  }, () => {
    setButtonsContentDefault('btn-primary');
  });
}

/**
 * Vrátí jen soubory, které Dropzone přijala (file.accepted !== false).
 *
 * 'addedfiles' obsahuje i soubory odmítnuté kontrolou Dropzone (příliš velké, nepovolený
 * typ) - dřív se odeslaly na BE a spadly tam (Sentry CLIENT-APP-BACKEND-4Y). Hlášku
 * o odmítnutí zobrazí CustomDropzone.
 *
 * @param {FileList|File[]} files soubory z eventu 'addedfiles'
 * @returns {File[]} přijaté soubory
 */
function acceptedDropzoneFiles(files) {
  return Array.from(files).filter(file => file.accepted !== false);
}

function submitIdentification() {
  const body = {
    ...step.value.formData,
    state: formDenied.value ? 'DENIED' : 'APPROVED',
    attachments: addedFiles.value
  };
  setButtonsContentLoading('btn-primary');
  IdentificationService.saveIdentification(loggedClient.value.id, body).then(response => {
    setButtonsContentSuccess('btn-primary');
    showSuccessMessage(response.message);
    nextStep();
  }, () => {
    setButtonsContentDefault('btn-primary');
  });
}

function submitForm() {
  const body = {
    ...step.value.formData,
    state: formDenied.value ? 'DENIED' : 'APPROVED'
  };
  setButtonsContentLoading('btn-primary');
  FormService.saveForm(loggedClient.value.id, body).then(response => {
    setButtonsContentSuccess('btn-primary');
    showSuccessMessage(response.message);
    nextStep();
  }, () => {
    setButtonsContentDefault('btn-primary');
  });
}

function countInvestmentQuestionnaireResult() {
  setButtonContentLoading('count-button');
  InvestmentQuestionnaireService.countResult(loggedClient.value.id, step.value.formData.id, step.value.formData).then(response => {
    result.value = response.questionnaire.result;
    setButtonContentDefault('count-button');
  }, () => {
    setButtonContentDefault('count-button');
  });
}

function countAmlQuestionnaireResult() {
  setButtonContentLoading('count-button');
  AmlQuestionnaireService.countResult(loggedClient.value.id, step.value.formData.id, step.value.formData).then(response => {
    result.value = response.questionnaire.result;
    setButtonContentDefault('count-button');
  }, () => {
    setButtonContentDefault('count-button');
  });
}

function nextStep() {
  result.value = null;
  formDenied.value = false;
  stepIndex.value++;
  step.value = steps.value[stepIndex.value];
  // scroll to top of the overlay
  const popup = document.getElementById('app-popup-title-element');
  if (popup) {
    popup.scrollIntoView({behavior: 'smooth'});
  }
}

function authenticateWithBankID() {
  checkForBankIdLogin();
  setButtonContentLoading('btn-bankid-login');
  getBankIdUrl('')
      .then(response => {
        localStorage.setItem('IDENTIFICATION_ID', step.value.id);
        if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Browser')) {
          void Browser.open({url: response.url});
        } else {
          window.location.href = response.url;
        }
      }, error => {
        setButtonContentDefault('btn-bankid-login');
        showErrorMessage(error.response?.data?.message);
      });
}

function endOnboarding() {
  emitter.emit('onboardingDone');
}

function removeFile(file) {
  addedFiles.value = addedFiles.value.filter(f => f.name !== file.name);
}
</script>

<template>
  <div class="app-popup">
    <div class="row h-100 overflow-auto justify-content-center align-items-center p-2" style="z-index: 1055">
      <div class="col-md-8">
        <div v-if="step.type !== 'end'" id="app-popup-title-element" class="app-popup__title">
          {{ t('clientOnboardingOverlay.requirements_to_processed') }}
        </div>
        <div class="app-popup__content">
          <template v-if="step.type === 'start'">
            <div class="row mb-3">
              <div class="col-12">
                {{ t('clientOnboardingOverlay.hello_requirements_to_processed') }}
              </div>
            </div>
            <div v-if="loggedUser?.meta?.show_form" class="row">
              <div class="col-12">
                <div class="alert alert-light d-flex justify-content-between">
                  <span>{{ t('clientOnboardingOverlay.confirm_your_data') }}</span>
                </div>
              </div>
            </div>
            <div v-for="investmentQuestionnaire of loggedUser?.waiting_investment_questionnaires" class="row">
              <div class="col-12">
                <div class="alert alert-light d-flex justify-content-between">
                  <span>{{ t('clientOnboardingOverlay.investment_questionnaire_to_complete') }}</span>
                </div>
              </div>
            </div>
            <div v-for="record of loggedUser?.waiting_meeting_records" class="row">
              <div class="col-12">
                <div class="alert alert-light d-flex justify-content-between">
                  <span>{{ t('clientOnboardingOverlay.meeting_records_to_complete') }}</span>
                </div>
              </div>
            </div>
            <div v-for="record of loggedUser?.waiting_aml_questionnaires" class="row">
              <div class="col-12">
                <div class="alert alert-light d-flex justify-content-between">
                  <span>{{ t('clientOnboardingOverlay.aml_questionnaire_to_complete') }}</span>
                </div>
              </div>
            </div>
            <div v-for="record of loggedUser?.waiting_identifications" class="row">
              <div class="col-12">
                <div class="alert alert-light d-flex justify-content-between">
                  <span>{{ t('clientOnboardingOverlay.identification_to_complete') }}</span>
                </div>
              </div>
            </div>
            <div v-for="record of loggedUser?.waiting_forms" class="row">
              <div class="col-12">
                <div class="alert alert-light d-flex justify-content-between">
                  <span>{{ t('clientOnboardingOverlay.form_to_complete') }}</span>
                </div>
              </div>
            </div>
            <div v-if="loggedUser?.meta?.documents_to_sign > 0" class="row">
              <div class="col-12">
                <div class="alert alert-light d-flex justify-content-between text-decoration-none">
                  <span>{{ t('clientOnboardingOverlay.unsigned_documents') }}</span>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-12 text-end">
                <button class="btn btn-primary btn-md" type="button" @click="nextStep">{{ t('clientOnboardingOverlay.next_step') }}</button>
              </div>
            </div>
          </template>
          <template v-if="step.type === 'complex_form'">
            <Form v-on:submit="submitComplexForm">
              <fieldset>
                <legend>{{ t('clientOnboardingOverlay.personal_details') }}</legend>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="firstnameInput" class="form-label">{{ t('clientOnboardingOverlay.name') }}</label>
                    <Field v-model="step.formData.client.firstname" id="firstnameInput" name="firstnameInput" class="form-control" rules="required"/>
                    <ErrorMessage name="firstnameInput"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="surnameInput" class="form-label">{{ t('clientOnboardingOverlay.surname') }}</label>
                    <Field v-model="step.formData.client.surname" id="surnameInput" name="surnameInput" class="form-control" rules="required"/>
                    <ErrorMessage name="surnameInput"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="titleBeforeInput" class="form-label">{{ t('clientOnboardingOverlay.title_before_name') }}</label>
                    <input v-model="step.formData.client.title_before" id="titleBeforeInput" class="form-control" type="text"/></div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="titleAfterInput" class="form-label">{{ t('clientOnboardingOverlay.title_behind_name') }}</label>
                    <input v-model="step.formData.client.title_after" id="titleAfterInput" class="form-control" type="text"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="emailInput" class="form-label">{{ t('clientOnboardingOverlay.email') }}</label>
                    <Field v-model="step.formData.client.email" id="emailInput" name="emailInput" class="form-control" type="email" rules="required|email"/>
                    <ErrorMessage name="emailInput"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="phoneInput" class="form-label">{{ t('clientOnboardingOverlay.phone_number') }}</label>
                    <Field v-model="step.formData.client.phone" id="phoneInput" name="phoneInput" class="form-control" type="text" rules="required|phoneNumber"/>
                    <ErrorMessage name="phoneInput"/>
                  </div>
                </div>
                <div class="mb-3">
                  <label for="companyInput" class="form-label">{{ t('clientOnboardingOverlay.company_name') }}</label>
                  <input v-model="step.formData.client.company_name" id="companyInput" class="form-control" type="text"/>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="form.clientTypeInput" class="form-label">{{ t('clientOnboardingOverlay.client_type') }}</label>
                    <input v-model="step.formData.client.client_type" id="form.clientTypeInput" class="form-control" type="text"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="employerTypeInput" class="form-label">{{ t('clientOnboardingOverlay.employer_type') }}</label>
                    <input v-model="step.formData.client.employer_type" id="employerTypeInput" class="form-control" type="text"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="birthDateInput" class="form-label">{{ t('clientOnboardingOverlay.birth_date') }}</label>
                    <input v-model="step.formData.client.birth_date" id="birthDateInput" class="form-control" type="date"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="nationalityInput" class="form-label">{{ t('clientOnboardingOverlay.nationality') }}</label>
                    <input v-model="step.formData.client.nationality" id="nationalityInput" class="form-control" type="text"/>
                  </div>
                </div>
              </fieldset>

              <fieldset class="mt-3">
                <legend>{{ t('clientOnboardingOverlay.addresses') }}</legend>
                <strong>{{ t('clientOnboardingOverlay.permanent_address') }}</strong>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="streetInput" class="form-label">{{ t('clientOnboardingOverlay.street') }}</label>
                    <input v-model="step.formData.domicile_address.street" id="streetInput" class="form-control"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="cityInput" class="form-label">{{ t('clientOnboardingOverlay.city') }}</label>
                    <input v-model="step.formData.domicile_address.city" id="cityInput" class="form-control"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="zipCodeInput" class="form-label">{{ t('clientOnboardingOverlay.zip_code') }}</label>
                    <input v-model="step.formData.domicile_address.zip_code" id="zipCodeInput" class="form-control" type="text"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="countyInput" class="form-label">{{ t('clientOnboardingOverlay.country') }}</label>
                    <input v-model="step.formData.domicile_address.country" id="countyInput" class="form-control" type="text"/>
                  </div>
                </div>
                <div class="row">
                  <label for="noteInput" class="form-label">{{ t('clientOnboardingOverlay.note') }}</label>
                  <input v-model="step.formData.domicile_address.note" id="noteInput" class="form-control" type="text"/>
                </div>

                <div class="form-check mt-3 mb-3">
                  <input v-model="step.formData.same_correspondence" id="same_correspondence" type="checkbox" class="form-check-input"/>
                  <label for="same_correspondence" class="form-check-label">{{ t('clientOnboardingOverlay.correspondence_address_same') }}</label>
                </div>

                <div v-if="!step.formData.same_correspondence"><strong>{{ t('clientOnboardingOverlay.correspondence_address') }}</strong>
                  <div class="row">
                    <div class="col-12 col-md-6 form-group">
                      <label for="streetInputCorrespondence" class="form-label">{{ t('clientOnboardingOverlay.adress') }}</label>
                      <input v-model="step.formData.correspondence_address.street" id="streetInput" class="form-control"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="cityInputCorrespondence" class="form-label">{{ t('clientOnboardingOverlay.city') }}</label>
                      <input v-model="step.formData.correspondence_address.city" id="cityInput" class="form-control"/>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12 col-md-6 form-group">
                      <label for="zipCodeInputCorrespondence" class="form-label">{{ t('clientOnboardingOverlay.zip_code') }}</label>
                      <input v-model="step.formData.correspondence_address.zip_code" id="zipCodeInput" class="form-control" type="text"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="countyInputCorrespondence" class="form-label">{{ t('clientOnboardingOverlay.country') }}</label>
                      <input v-model="step.formData.correspondence_address.country" id="countyInput" class="form-control" type="text"/>
                    </div>
                  </div>
                  <div class="row">
                    <label for="noteInput" class="form-label">{{ t('clientOnboardingOverlay.note') }}</label>
                    <input v-model="step.formData.correspondence_address.note" id="noteInput" class="form-control" type="text"/>
                  </div>
                </div>
              </fieldset>

              <fieldset class="mt-3">
                <legend>{{ t('clientOnboardingOverlay.bank_account') }}</legend>
                <div class="row">
                  <div class="col-12 col-md-6 form-group" :hidden="step.formData.bank_account.currency !== 'CZK'">
                    <label for="accountNumberInput" class="form-label">{{ t('clientOnboardingOverlay.account_number') }}</label>
                    <Field v-model="step.formData.bank_account.account_number" id="accountNumberInput" name="accountNumberInput" class="form-control" :rules="step.formData.bank_account.currency === 'CZK' ? 'required' : ''"/>
                    <ErrorMessage name="accountNumberInput"/>
                  </div>
                  <div class="col-12 col-md-6 form-group" :hidden="step.formData.bank_account.currency !== 'CZK'">
                    <label for="bankCodeInput" class="form-label">{{ t('clientOnboardingOverlay.bank_code') }}</label>
                    <Field v-model="step.formData.bank_account.bank_code" id="bankCodeInput" name="bankCodeInput" class="form-control" :rules="step.formData.bank_account.currency === 'CZK' ? 'required' : ''"/>
                    <ErrorMessage name="bankCodeInput"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group" :hidden="step.formData.bank_account.currency === 'CZK'">
                    <label for="ibanInput" class="form-label">{{ t('clientOnboardingOverlay.iban') }}</label>
                    <Field v-model="step.formData.bank_account.iban" id="ibanInput" name="ibanInput" class="form-control" :rules="step.formData.bank_account.currency !== 'CZK' ? 'required' : ''"/>
                    <ErrorMessage name="ibanInput"/>
                  </div>
                  <div class="col-12 col-md-6 form-group" :hidden="step.formData.bank_account.currency === 'CZK'">
                    <label for="bicInput" class="form-label">{{ t('clientOnboardingOverlay.bic_swift') }}</label>
                    <Field v-model="step.formData.bank_account.bic" id="bicInput" name="bicInput" class="form-control" :rules="step.formData.bank_account.currency !== 'CZK' ? 'required' : ''"/>
                    <ErrorMessage name="bicInput"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="currencySelect" class="form-label">{{ t('clientOnboardingOverlay.currency') }}</label>
                    <select v-model="step.formData.bank_account.currency" id="currencySelect" class="form-control">
                      <option value="CZK">CZK</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 text-end">
                    <button class="btn btn-primary btn-md"><span>{{ t('clientOnboardingOverlay.save') }}</span></button>
                  </div>
                </div>
              </fieldset>
            </Form>
          </template>
          <template v-if="step.type === 'investment_questionnaire'">
            <form @submit.prevent="submitInvestmentQuestionnaire" :key="step.id">
              <template v-if="!result">
                <div class="row mb-3">
                  <div class="col-12">
                    {{ t('clientOnboardingOverlay.fill_investment_questionnaire') }}
                  </div>
                </div>
                <div v-if="step.formData?.id && !props.disabled" class="modal-top-footer mb-3">
                  <button v-if="step.formData?.id && !props.disabled" class="btn btn-primary btn-md ms-4" type="submit"><span>{{ t('clientOnboardingOverlay.confirm') }}</span></button>
                </div>
                <div class="modal-body">
                  <DynamicForm :disabled="false" :config="step.formData.data" ref="dynamicFormRef"></DynamicForm>
                  <div v-if="formDenied" class="row">
                    <div class="col-12 form-group">
                      <label for="denied_description" class="form-label">{{ t('clientOnboardingOverlay.refusal_reason') }}</label>
                      <textarea type="text" v-model="step.formData.denied_description" class="form-control" id="denied_description" rows="3"></textarea>
                    </div>
                  </div>
                </div>
              </template>
              <div v-if="result" class="modal-body">
                <fieldset>
                  <div class="row mb-3">
                    <div class="col-12">
                      <legend>{{ t('investmentQuestionnaireModal.result') }}</legend>
                    </div>
                    <div class="col-12">
                      <hr/>
                      <table class="table">
                        <thead>
                          <tr>
                            <th scope="col">{{ t('investmentQuestionnaireModal.metric') }}</th>
                            <th scope="col">{{ t('investmentQuestionnaireModal.review') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="key in visibleInvestmentQuestionnaireResultKeys">
                            <td scope="row">{{ t('investmentQuestionnaireModal.' + key) }}</td>
                            <!-- Pokud existuje překlad pro hodnotu (včetně true/false → "Ano"/"Ne"), použijeme ho; jinak zobrazíme surovou hodnotu -->
                            <td>{{ te('investmentQuestionnaireModal.' + result?.[key]) ? t('investmentQuestionnaireModal.' + result?.[key]) : (result?.[key] ?? '-') }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </fieldset>
              </div>
              <div class="modal-footer">
                <button v-if="step.formData?.id && formDenied" class="btn btn-link text-decoration-underline text-dark" type="button" @click="formDenied = false">{{ t('clientOnboardingOverlay.back') }}</button>
                <button v-if="step.formData?.id && !formDenied && !result" class="btn btn-link btn-text-red btn-md" @click="formDenied = true" type="button">{{ t('clientOnboardingOverlay.reject') }}</button>
                <button v-if="step.formData?.id && !formDenied && result" class="btn btn-link btn-md" @click="result = null" type="button">{{ t('clientOnboardingOverlay.edit_form') }}</button>
                <button v-if="step.formData?.id && !formDenied && !result" class="btn btn-primary btn-md ms-4" @click="countInvestmentQuestionnaireResult()" type="button" id="count-button"><span>{{ t('clientOnboardingOverlay.evaluate') }}</span></button>
                <button v-if="step.formData?.id && formDenied && !result" class="btn btn-primary btn-md ms-4" @click="submitInvestmentQuestionnaire" type="button" id="deny-button"><span>{{ t('clientOnboardingOverlay.confirm_reject') }}</span></button>
                <button v-if="step.formData?.id && !formDenied && result" class="btn btn-primary btn-md ms-4" type="submit" id="submit-button"><span>{{ t('clientOnboardingOverlay.send') }}</span></button>
              </div>
            </form>
          </template>
          <template v-if="step.type === 'meeting_record'">
            <div class="row mb-3">
              <div class="col-12">
                {{ t('clientOnboardingOverlay.fill_meeting_records') }}
              </div>
            </div>
            <form @submit.prevent="submitMeetingRecord" :key="step.id">
              <div v-if="step.formData?.id && !props.disabled" class="modal-top-footer">
                <button v-if="step.formData?.id && !props.disabled" class="btn btn-primary btn-md ms-4" type="submit"><span>{{ t('clientOnboardingOverlay.confirm') }}</span></button>
              </div>
              <div class="modal-body">
                <DynamicForm :disabled="true" :config="step.formData.data" ref="dynamicFormRef"></DynamicForm>
                <div v-if="formDenied" class="row">
                  <div class="col-12 form-group">
                    <label for="denied_description" class="form-label">{{ t('clientOnboardingOverlay.refusal_reason') }}</label>
                    <textarea type="text" v-model="step.formData.denied_description" class="form-control" id="denied_description" rows="3"></textarea>
                  </div>
                </div>
              </div>

              <div v-if="props.result" class="mb-3">
                <hr/>
                <label class="form-check-label me-3">{{ t('clientOnboardingOverlay.result') }}</label>
                <input type="text" v-model="props.result" disabled/>
              </div>
              <div class="modal-footer">
                <button v-if="step.formData?.id && formDenied" class="btn btn-link text-decoration-underline text-dark" type="button" @click="formDenied = false">{{ t('clientOnboardingOverlay.back') }}</button>
                <button v-if="step.formData?.id && !formDenied" class="btn btn-link btn-text-red btn-md" @click="formDenied = true" type="button">{{ t('clientOnboardingOverlay.reject') }}</button>
                <button v-if="step.formData?.id && formDenied" class="btn btn-primary btn-md ms-4" @click="submitMeetingRecord" type="button"><span>{{ t('clientOnboardingOverlay.confirm_reject') }}</span></button>
                <button v-if="step.formData?.id && !formDenied" class="btn btn-primary btn-md ms-4" type="submit"><span>{{ t('clientOnboardingOverlay.confirm') }}</span></button>
              </div>
            </form>
          </template>
          <template v-if="step.type === 'aml_questionnaire'">

            <form @submit.prevent="submitAmlQuestionnaire" :key="step.id">
              <template v-if="!result">
                <div class="row mb-3">
                  <div class="col-12">
                    {{ t('clientOnboardingOverlay.fill_aml_questionnaire') }}
                  </div>
                </div>
                <div v-if="step.formData?.id && !props.disabled" class="modal-top-footer">
                  <button v-if="step.formData?.id && !props.disabled" class="btn btn-primary btn-md ms-4" type="submit"><span>{{ t('clientOnboardingOverlay.confirm') }}</span></button>
                </div>
                <div class="modal-body">
                  <DynamicForm :disabled="false" :config="step.formData.data" ref="dynamicFormRef"></DynamicForm>
                  <div v-if="formDenied" class="row">
                    <div class="col-12 form-group">
                      <label for="denied_description" class="form-label">{{ t('clientOnboardingOverlay.refusal_reason') }}</label>
                      <textarea type="text" v-model="step.formData.denied_description" class="form-control" id="denied_description" rows="3"></textarea>
                    </div>
                  </div>
                </div>
              </template>
              <div v-if="result" class="modal-body">
                <fieldset>
                  <div class="row mb-3">
                    <div class="col-12">
                      <legend>{{ t('AmlQuestionnaireModal.result') }}</legend>
                    </div>
                    <div class="col-12">
                      <hr/>
                      <table class="table">
                        <thead>
                          <tr>
                            <th scope="col">{{ t('AmlQuestionnaireModal.metric') }}</th>
                            <th scope="col">{{ t('AmlQuestionnaireModal.review') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="key in visibleAMLQuestionnaireResultKeys">
                            <td scope="row" :class="{ 'fw-bold': key === 'CLIENT_CHECK_INTENSITY' }">{{ t('AmlQuestionnaireModal.' + key) }}</td>
                            <td>{{ result[key] }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </fieldset>
              </div>
              <div class="modal-footer">
                <button v-if="step.formData?.id && formDenied" class="btn btn-link text-decoration-underline text-dark" type="button" @click="formDenied = false">{{ t('clientOnboardingOverlay.back') }}</button>
                <button v-if="step.formData?.id && !formDenied && !result" class="btn btn-link btn-text-red btn-md" @click="formDenied = true" type="button">{{ t('clientOnboardingOverlay.reject') }}</button>
                <button v-if="step.formData?.id && !formDenied && result" class="btn btn-link btn-md" @click="result = null" type="button">{{ t('clientOnboardingOverlay.edit_form') }}</button>
                <button v-if="step.formData?.id && !formDenied && !result" class="btn btn-primary btn-md ms-4" @click="countAmlQuestionnaireResult()" type="button" id="count-button"><span>{{ t('clientOnboardingOverlay.evaluate') }}</span></button>
                <button v-if="step.formData?.id && formDenied && !result" class="btn btn-primary btn-md ms-4" @click="submitAmlQuestionnaire" type="button"><span>{{ t('clientOnboardingOverlay.confirm_reject') }}</span></button>
                <button v-if="step.formData?.id && !formDenied && result" class="btn btn-primary btn-md ms-4" type="submit"><span>{{ t('clientOnboardingOverlay.confirm') }} </span></button>
              </div>
            </form>
          </template>
          <template v-if="step.type === 'identification'">
            <template v-if="step.formData?.method === 'IDCARD'">
              <div class="row mb-3">
                <div class="col-12">
                  {{ t('clientOnboardingOverlay.upload_id_card') }}
                </div>
              </div>
              <div class="modal-body">
                <div class="row h-100 justify-content-center align-items-center">
                  <div class="col-12">
                    <div class="row">
                      <div class="col-12 form-group mb-3">
                        <CustomDropzone :dropzone-options-input="{
                                  autoProcessQueue: false,
                                  url: getApiBaseUrl() + '/api/files/upload',
                                  acceptedFiles: '.jpg, .jpeg, .png, .pdf, .docx, .xlsx',
                                  maxFiles: 20,
                                  uploadMultiple: true,
                                  init() {
                                    this.on('addedfiles', function (files, xhr, formData) {
                                      addedFiles = [...acceptedDropzoneFiles(files), ...addedFiles];
                                    });
                                    },
                                }" :previews-disabled="true"/>
                      </div>
                      <div class="col-12">
                        <div v-for="file of addedFiles" class="row">
                          <div class="col-6">
                            <span>{{ file.name }}</span>
                          </div>
                          <div class="col-6">
                            <button type="button" class="btn btn-link btn-text-red" @click="removeFile(file)">{{ t('clientOnboardingOverlay.remove') }}</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button v-if="step.formData?.id && !formDenied" :disabled="addedFiles?.length === 0" class="btn btn-primary btn-md ms-4" type="button" @click="submitIdentification"><span>{{ t('clientOnboardingOverlay.confirm') }} </span></button>
              </div>
            </template>
            <template v-if="step.formData?.method === 'BANKID'">
              <!-- IMA-519: upozornění na odstávku Bank iD u identifikace přes Bank iD -->
              <AppAnnouncements :onlyBankId="true"/>
              <div class="row mb-3">
                <div class="col-">
                  {{ t('clientOnboardingOverlay.identify_via_bankid') }}
                </div>
              </div>
              <div class="row">
                <div class="col-12 text-center">
                  <button id="btn-bankid-login" type="button" @click="authenticateWithBankID()" style="max-width: 500px" class="btn btn--with-icon btn--bank-id">
                    <span class="btn-icon"></span>
                    <span class="btn-content">{{ t('clientOnboardingOverlay.identify_via_bankid2') }}</span>
                  </button>
                </div>
              </div>
            </template>
          </template>
          <template v-if="step.type === 'form'">
            <div class="row mb-3">
              <div class="col-12">
                {{ t('clientOnboardingOverlay.fill_form') }}
              </div>
            </div>
            <form @submit.prevent="submitForm" :key="step.id">
              <div v-if="step.formData?.id && !props.disabled" class="modal-top-footer">
                <button v-if="step.formData?.id && !props.disabled" class="btn btn-primary btn-md ms-4" type="submit"><span>{{ t('clientOnboardingOverlay.confirm') }}</span></button>
              </div>
              <div class="modal-body">
                <DynamicForm :disabled="false" :config="step.formData.data" ref="dynamicFormRef"></DynamicForm>
                <div v-if="formDenied" class="row">
                  <div class="col-12 form-group">
                    <label for="denied_description" class="form-label">{{ t('clientOnboardingOverlay.refusal_reason') }}</label>
                    <textarea type="text" v-model="step.formData.denied_description" class="form-control" id="denied_description" rows="3"></textarea>
                  </div>
                </div>
              </div>

              <div v-if="props.result" class="mb-3">
                <hr/>
                <label class="form-check-label me-3">{{ t('clientOnboardingOverlay.result') }}</label>
                <input type="text" v-model="props.result" disabled/>
              </div>
              <div class="modal-footer">
                <button v-if="step.formData?.id && formDenied" class="btn btn-link text-decoration-underline text-dark" type="button" @click="formDenied = false">{{ t('clientOnboardingOverlay.back') }}</button>
                <button v-if="step.formData?.id && !formDenied" class="btn btn-link btn-text-red btn-md" @click="formDenied = true" type="button">{{ t('clientOnboardingOverlay.reject') }}</button>
                <button v-if="step.formData?.id && formDenied" class="btn btn-primary btn-md ms-4" @click="submitForm" type="button"><span>{{ t('clientOnboardingOverlay.confirm_reject') }}</span></button>
                <button v-if="step.formData?.id && !formDenied" class="btn btn-primary btn-md ms-4" type="submit"><span>{{ t('clientOnboardingOverlay.confirm') }}</span></button>
              </div>
            </form>
          </template>
          <template v-if="step.type === 'document_sign'">
            <div class="row mb-3">
              <div class="col-12">
                {{ t('clientOnboardingOverlay.sign_documents') }}
              </div>
            </div>
            <DocumentsListMinimalComponent></DocumentsListMinimalComponent>
          </template>
          <template v-if="step.type === 'end'">
            <div class="row mb-3">
              <div class="col-12 text-center">
                <i class="mdi mdi-check icon-primary icon-big"></i>
              </div>
              <h3 class="col-12 mt-5 mb-5 text-center">
                {{ t('clientOnboardingOverlay.thanks_sign_done2') }}
              </h3>
            </div>
            <div class="row">
              <div class="col-12 text-center">
                <router-link to="/payment-terms" class="btn btn-primary btn-lg" type="button" @click="endOnboarding">{{ t('clientOnboardingOverlay.deposit_funds') }}</router-link>
                <br/>
                <button class="btn btn-link btn-lg mt-5" type="button" @click="endOnboarding">{{ t('clientOnboardingOverlay.close') }}</button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

